import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RefreshCcw, Link as LinkIcon, AlertTriangle } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, resolveImage, formatPrice, formatError } from "@/lib/api";

const CONDITIONS = ["Nuovo", "Usato", "Vintage"];

export default function ImportEbay() {
  const [status, setStatus] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState({ loaded: 0, total: 0, page: 0, pages: 0 });
  const [selected, setSelected] = useState(new Set());
  const [edits, setEdits] = useState({}); // item_id -> partial overrides
  const [cats, setCats] = useState([]);
  const [step, setStep] = useState("list"); // list | review

  const loadCats = async () => {
    const { data } = await api.get("/categories", { params: { include_hidden: true } });
    setCats(data);
  };

  const loadStatus = async () => {
    const { data } = await api.get("/admin/ebay/status");
    setStatus(data);
  };

  const loadListings = async (opts = {}) => {
    const { refresh = false } = opts;
    setLoading(true);
    setListings([]);
    setLoadProgress({ loaded: 0, total: 0, page: 0, pages: 0 });
    try {
      let page = 1;
      const perPage = 25;
      let totalPages = 1;
      let allItems = [];
      // Progressive pagination: fetch pages one at a time and append.
      while (page <= totalPages) {
        const { data } = await api.get("/admin/ebay/listings", {
          params: { page, per_page: perPage, refresh: refresh && page === 1 ? true : undefined },
        });
        if (data.error_hint) {
          toast.error("Attenzione eBay", { description: data.error_hint });
          if (data.stale) {
            allItems = allItems.concat(data.items || []);
            setListings([...allItems]);
          }
          break;
        }
        allItems = allItems.concat(data.items || []);
        totalPages = data.pages || 1;
        setListings([...allItems]);
        setLoadProgress({ loaded: allItems.length, total: data.total || allItems.length, page, pages: totalPages });
        page += 1;
        if (page > 200) break; // safety guard
      }
    } catch (e) {
      toast.error("Errore caricamento", { description: formatError(e.response?.data?.detail) || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await loadCats();
      const s = await api.get("/admin/ebay/status").then((r) => r.data).catch(() => null);
      setStatus(s);
      if (s && (s.mock_mode || s.connected)) {
        loadListings();
      } else {
        setListings([]);
      }
    })();
    // Toast on OAuth return
    const url = new URL(window.location.href);
    if (url.searchParams.get("connected") === "1") {
      toast.success("Account eBay collegato correttamente");
      url.searchParams.delete("connected");
      window.history.replaceState({}, "", url.pathname + (url.search || ""));
    }
    const err = url.searchParams.get("ebay_error");
    if (err) {
      toast.error("Collegamento eBay non riuscito", { description: err });
      url.searchParams.delete("ebay_error");
      window.history.replaceState({}, "", url.pathname + (url.search || ""));
    }
  }, []);

  const connect = async () => {
    try {
      const { data } = await api.post("/admin/ebay/connect");
      window.location.href = data.url;
    } catch (e) {
      toast.error("Impossibile avviare OAuth", { description: formatError(e.response?.data?.detail) || e.message });
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnettere l'account eBay?")) return;
    await api.post("/admin/ebay/disconnect");
    toast.success("Account eBay disconnesso");
    loadStatus();
  };

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const selectableItems = listings.filter((it) => !it.already_imported);

  const toggleAll = () => {
    if (selected.size === selectableItems.length) setSelected(new Set());
    else setSelected(new Set(selectableItems.map((i) => i.item_id)));
  };

  const editField = (id, key, val) => {
    setEdits((e) => ({ ...e, [id]: { ...(e[id] || {}), [key]: val } }));
  };

  const merged = (it) => ({
    ...it,
    ...(edits[it.item_id] || {}),
    proposal: { ...it.proposal, ...(edits[it.item_id]?.proposal || {}) },
  });

  const goReview = () => {
    if (selected.size === 0) {
      toast.error("Seleziona almeno una inserzione");
      return;
    }
    setStep("review");
  };

  const doImport = async (publish) => {
    const selectedItems = listings.filter((it) => selected.has(it.item_id));
    const items = selectedItems.map((it) => {
      const m = merged(it);
      return {
        item_id: m.item_id,
        title: m.title,
        description: m.description || "",
        price: parseFloat(m.price),
        quantity: parseInt(m.quantity),
        condition: m.proposal.condition,
        brand: m.proposal.brand || "",
        year: m.proposal.year || null,
        sku: m.sku || null,
        images: m.photos || [],
        category_id: m.proposal.category_id,
        ebay_url: m.url || null,
        ebay_category: m.category || null,
        external_ids: { ebay_category_id: m.category_id || null },
        publish,
      };
    });
    if (items.some((i) => !i.category_id)) {
      toast.error("Seleziona una sottocategoria per ogni prodotto");
      return;
    }
    try {
      const { data } = await api.post("/admin/ebay/import", { items });
      toast.success(`Importati ${data.created.length} prodotti · ${data.skipped.length} saltati`);
      setSelected(new Set());
      setEdits({});
      setStep("list");
      loadListings();
    } catch (e) {
      toast.error("Errore importazione", { description: formatError(e.response?.data?.detail) || e.message });
    }
  };

  // Grouped subcategories for dropdowns
  const roots = cats.filter((c) => !c.parent_id);
  const subsFor = (rootId) => cats.filter((c) => c.parent_id === rootId);

  if (step === "review") {
    const selectedItems = listings.filter((it) => selected.has(it.item_id));
    return (
      <AdminLayout title="Revisione importazione" back="/admin/prodotti">
        <button onClick={() => setStep("list")} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500">
          <ArrowLeft className="w-4 h-4" /> Indietro alla selezione
        </button>
        <p className="text-sm text-slate-500 mb-4">
          Controlla e modifica i dati proposti prima di importare. I prodotti in "Da classificare" verranno salvati come bozza.
        </p>
        <div className="space-y-3">
          {selectedItems.map((it) => {
            const m = merged(it);
            const rootId = m.proposal.root_id;
            const availableSubs = rootId ? subsFor(rootId) : [];
            return (
              <div key={it.item_id} data-testid={`review-item-${it.item_id}`} className="bg-white border border-border rounded-2xl p-4">
                <div className="flex gap-3">
                  <div className="w-20 h-20 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                    {m.photos?.[0] && <img src={m.photos[0]} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-400 font-mono">{m.item_id}</div>
                    <Input
                      data-testid={`review-title-${it.item_id}`}
                      value={m.title}
                      onChange={(e) => editField(it.item_id, "title", e.target.value)}
                      className="mt-1 rounded-xl text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                  <div>
                    <Label className="text-xs">Categoria principale</Label>
                    <Select
                      value={m.proposal.root_id || ""}
                      onValueChange={(v) => {
                        editField(it.item_id, "proposal", { ...m.proposal, root_id: v, category_id: null });
                      }}
                    >
                      <SelectTrigger data-testid={`review-root-${it.item_id}`} className="mt-1 rounded-xl h-10">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent>
                        {roots.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Sottocategoria</Label>
                    <Select
                      value={m.proposal.category_id || ""}
                      onValueChange={(v) => editField(it.item_id, "proposal", { ...m.proposal, category_id: v })}
                    >
                      <SelectTrigger data-testid={`review-sub-${it.item_id}`} className="mt-1 rounded-xl h-10">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableSubs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Condizione</Label>
                    <Select
                      value={m.proposal.condition}
                      onValueChange={(v) => editField(it.item_id, "proposal", { ...m.proposal, condition: v })}
                    >
                      <SelectTrigger data-testid={`review-cond-${it.item_id}`} className="mt-1 rounded-xl h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Prezzo €</Label>
                    <Input
                      data-testid={`review-price-${it.item_id}`}
                      type="number"
                      step="0.01"
                      value={m.price}
                      onChange={(e) => editField(it.item_id, "price", e.target.value)}
                      className="mt-1 rounded-xl h-10"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Quantità</Label>
                    <Input
                      data-testid={`review-qty-${it.item_id}`}
                      type="number"
                      value={m.quantity}
                      onChange={(e) => editField(it.item_id, "quantity", e.target.value)}
                      className="mt-1 rounded-xl h-10"
                    />
                  </div>
                </div>
                {m.proposal.needs_review && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-lg p-2">
                    <AlertTriangle className="w-4 h-4" />
                    Categoria non riconosciuta con sicurezza — verrà salvato come bozza per revisione manuale.
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 sticky bottom-0 bg-background pt-3 pb-4">
          <Button data-testid="import-draft-btn" onClick={() => doImport(false)} variant="outline" className="rounded-full h-12">
            Salva tutto come bozza
          </Button>
          <Button data-testid="import-publish-btn" onClick={() => doImport(true)} className="rounded-full h-12 bg-brand-yellow hover:bg-brand-yellow-dark text-brand-blue font-heading font-bold">
            Importa e pubblica
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Importa da eBay" back="/admin/prodotti">
      <div className="mb-4 bg-white border border-border rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase font-bold tracking-widest text-slate-500">Stato connessione</div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {status?.mock_mode ? (
              <Badge className="bg-amber-400 text-amber-900">MODALITÀ DEMO / MOCK</Badge>
            ) : status?.connected ? (
              <Badge className="bg-emerald-600 text-white">Connesso</Badge>
            ) : (
              <Badge className="bg-slate-400 text-white">Non connesso</Badge>
            )}
            <span className="text-xs text-slate-500">Ambiente: {status?.environment}</span>
            {status && !status.creds_configured && !status.mock_mode && (
              <Badge variant="outline" className="text-xs">Credenziali non configurate</Badge>
            )}
          </div>
          {status?.mock_mode && (
            <p className="text-xs text-slate-500 mt-2">
              Vengono mostrate inserzioni di esempio. Appena inserirai le credenziali eBay in <code>.env</code> (EBAY_CLIENT_ID / EBAY_CLIENT_SECRET / EBAY_RU_NAME) e imposterai <code>EBAY_USE_MOCK=false</code>, il sistema userà le tue inserzioni reali senza modifiche al codice.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button data-testid="reload-btn" onClick={() => loadListings({ refresh: true })} variant="outline" className="rounded-full" disabled={loading}>
            <RefreshCcw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> {loading ? "Caricamento..." : "Ricarica"}
          </Button>
          {!status?.mock_mode && status?.creds_configured && !status?.connected && (
            <Button data-testid="connect-btn" onClick={connect} className="rounded-full bg-brand-blue text-white">
              <LinkIcon className="w-4 h-4 mr-1" /> Collega eBay
            </Button>
          )}
          {!status?.mock_mode && status?.connected && (
            <Button data-testid="disconnect-btn" onClick={disconnect} variant="outline" className="rounded-full">
              Disconnetti
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-500" data-testid="listings-counter">
          {loading && loadProgress.total > 0
            ? `Caricamento pagina ${loadProgress.page}/${loadProgress.pages} · ${loadProgress.loaded}/${loadProgress.total} inserzioni`
            : `${listings.length} inserzioni · ${selectableItems.length} importabili`}
        </div>
        <button
          data-testid="select-all-btn"
          onClick={toggleAll}
          className="text-xs font-bold text-brand-blue underline"
        >
          {selected.size === selectableItems.length && selectableItems.length > 0 ? "Deseleziona tutto" : "Seleziona tutte"}
        </button>
      </div>

      {loading && listings.length === 0 ? (
        <div className="text-center text-slate-500 py-12">Caricamento inserzioni...</div>
      ) : (
        <div className="space-y-3">
          {listings.map((it) => {
            const isSel = selected.has(it.item_id);
            const disabled = it.already_imported;
            return (
              <div
                key={it.item_id}
                data-testid={`listing-${it.item_id}`}
                className={`bg-white border rounded-2xl p-3 flex gap-3 ${disabled ? "opacity-60" : ""} ${isSel ? "border-brand-blue" : "border-border"}`}
              >
                <div className="flex items-center pl-1">
                  <Checkbox
                    data-testid={`chk-${it.item_id}`}
                    checked={isSel}
                    onCheckedChange={() => !disabled && toggle(it.item_id)}
                    disabled={disabled}
                  />
                </div>
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                  {it.photos?.[0] && <img src={it.photos[0]} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate">{it.title}</div>
                  <div className="text-xs text-slate-500 truncate">
                    ID: {it.item_id} · {it.condition} · Qta {it.quantity_available}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <Badge className="bg-brand-blue text-white text-[10px]">{it.proposal.root_name || "?"}</Badge>
                    <Badge className={`text-[10px] ${it.proposal.needs_review ? "bg-orange-500 text-white" : "bg-slate-200 text-slate-700"}`}>
                      {it.proposal.sub_category_name}
                    </Badge>
                    {disabled && <Badge className="bg-amber-400 text-amber-900 text-[10px]">GIÀ IMPORTATO</Badge>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-heading font-black text-brand-blue">{formatPrice(it.price)}</div>
                </div>
              </div>
            );
          })}
          {loading && listings.length > 0 && (
            <div className="text-center text-xs text-slate-400 py-3" data-testid="progressive-loader">
              Caricamento pagine successive...
            </div>
          )}
        </div>
      )}

      <div className="mt-6 sticky bottom-0 bg-background py-3">
        <Button
          data-testid="review-btn"
          onClick={goReview}
          disabled={selected.size === 0}
          className="w-full h-12 rounded-full bg-brand-yellow hover:bg-brand-yellow-dark text-brand-blue font-heading font-bold"
        >
          Rivedi e importa ({selected.size} selezionati)
        </Button>
      </div>
    </AdminLayout>
  );
}
